import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import UserStoriesTable from "./UserStoriesTable";
import "../assets/css/DigixLeads.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";

async function fetchSubprojectsForProject(projectId) {
  if (!projectId) return [];
  let response = await axiosInstance.get(`/sub-projects/project/${projectId}`);
  let raw = response.data;
  let arr = [];
  let primaryParsed = false;
  if (Array.isArray(raw)) {
    arr = raw;
    primaryParsed = true;
  } else if (raw && Array.isArray(raw.sub_projects)) {
    arr = raw.sub_projects;
    primaryParsed = true;
  } else if (raw && Array.isArray(raw.data)) {
    arr = raw.data;
    primaryParsed = true;
  }
  if (!primaryParsed) {
    try {
      response = await axiosInstance.get(`/projects/${projectId}/sub-projects`);
      raw = response.data;
      arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
    } catch {
      arr = [];
    }
  }
  return (arr || [])
    .map((sp) => ({
      id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
      name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? "",
    }))
    .filter((x) => x.id != null && String(x.name).trim() !== "")
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function UserStoriesPage({ projects = [], onFetchProjects }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const selectedProject = useMemo(
    () =>
      projects.find(
        (p) => String(p.Project_Id ?? p.project_id ?? p.id) === String(selectedProjectId)
      ) || null,
    [projects, selectedProjectId]
  );
  const selectedProjectName =
    selectedProject?.Project_Name ?? selectedProject?.project_name ?? selectedProject?.name ?? "";

  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [selectedSubprojectId, setSelectedSubprojectId] = useState("");
  const selectedSubprojectName = useMemo(() => {
    if (!selectedSubprojectId) return "";
    const sp = subprojects.find((s) => String(s.id) === String(selectedSubprojectId));
    return sp?.name || "";
  }, [selectedSubprojectId, subprojects]);

  const userStoriesKeyRef = useRef(0);
  const [userStoriesRefreshKey, setUserStoriesRefreshKey] = useState(0);

  useEffect(() => {
    const s = location.state;
    if (!s || typeof s !== "object") return;
    if (s.resetUserStoryFilters) {
      setSelectedProjectId("");
      setSelectedSubprojectId("");
      navigate(".", { replace: true, state: null });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (typeof onFetchProjects === "function") onFetchProjects({});
  }, [onFetchProjects]);

  useEffect(() => {
    let cancelled = false;
    setSelectedSubprojectId("");
    const load = async () => {
      if (!selectedProjectId) {
        setSubprojects([]);
        return;
      }
      setLoadingSubprojects(true);
      try {
        const mapped = await fetchSubprojectsForProject(selectedProjectId);
        if (!cancelled) setSubprojects(mapped);
      } catch (err) {
        console.error("Error fetching subprojects:", err);
        if (!cancelled) setSubprojects([]);
      } finally {
        if (!cancelled) setLoadingSubprojects(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  return (
    <div className="projects-container project-management-container">
      <div className="main-card project-management-main-card">
        <div
          className="project-management-tab-content project-management-userstories-tab"
          aria-label="User stories"
        >
          <UserStoriesTable
            key={userStoriesRefreshKey}
            dataActive
            projectId={selectedProjectId ? Number(selectedProjectId) : null}
            subProjectId={selectedSubprojectId ? Number(selectedSubprojectId) : null}
            projectName={selectedProjectName}
            subProjectName={selectedSubprojectName}
            onRefresh={() => {
              userStoriesKeyRef.current += 1;
              setUserStoriesRefreshKey(userStoriesKeyRef.current);
            }}
            projects={projects}
            subprojects={subprojects}
            loadingSubprojects={loadingSubprojects}
            selectedProjectId={selectedProjectId}
            selectedSubprojectId={selectedSubprojectId}
            onProjectChange={(val) => {
              setSelectedProjectId(val);
              if (!val) setSelectedSubprojectId("");
            }}
            onSubprojectChange={setSelectedSubprojectId}
          />
        </div>
      </div>
    </div>
  );
}

export default UserStoriesPage;
